import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Loader2, ZoomIn, ZoomOut, Undo2, Move, X } from 'lucide-react';
import * as UntypedDialog from '@/components/ui/dialog';
import { iconDataUri } from '@/lib/sitePages';
import {
  openPicture, initialCrop, zoomCrop, moveCrop, zoomRange, cropStyle, drawCrop, encodeIcon,
  saveSiteIcon, restoreSiteIcon, isOwnIcon,
} from '@/lib/siteIcon';

// ─── Change a site's icon ─────────────────────────────────────────────────────
// Opened from the site dashboard (the icon on Overview, and Settings → Site
// icon). The owner picks a photo, places it in the square — drag or arrow keys
// to move, pinch, scroll, the slider or +/− to zoom — and sees it at the two
// sizes it will really be seen at before saving: a browser tab and a phone's
// home screen. src/lib/siteIcon.js does the cropping and the shrinking; only
// the finished 128px picture is sent.

// shadcn's forwardRef components have no prop types under checkJs.
const { Dialog, DialogContent, DialogTitle, DialogDescription } = /** @type {any} */ (UntypedDialog);

const CHECKER = {
  backgroundImage: 'conic-gradient(rgb(var(--zinc-800)) 25%, rgb(var(--zinc-900)) 0 50%, rgb(var(--zinc-800)) 0 75%, rgb(var(--zinc-900)) 0)',
  backgroundSize: '16px 16px',
};

/** A square showing the crop in progress, or the icon as it is now. */
function IconView({ picture = null, crop = null, icon = '', className = '', pad = '' }) {
  if (picture && crop) {
    return (
      <span className={`relative block overflow-hidden shrink-0 ${className}`}>
        <img src={picture.url} alt="" draggable={false} style={cropStyle(picture, crop)} className="pointer-events-none select-none" />
      </span>
    );
  }
  const src = iconDataUri(icon);
  if (!src) return <span className={`block shrink-0 bg-gradient-to-br from-orange-500/40 to-orange-600/10 ${className}`} />;
  // The owner's picture fills its square; a drawn mark gets room to breathe.
  return <img src={src} alt="" className={`block shrink-0 ${isOwnIcon(icon) ? 'object-cover' : `object-contain ${pad}`} ${className}`} />;
}

/** The icon at the sizes it is actually seen: a browser tab, a phone's home screen. */
function Previews({ picture = null, crop = null, icon = '', title = '' }) {
  const name = title || 'Your site';
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Browser tab</p>
        <div className="rounded-xl bg-zinc-950 border border-zinc-800 px-2 pt-2">
          <div className="flex items-center gap-2 h-8 rounded-t-lg bg-zinc-800 px-2.5 min-w-0">
            <IconView picture={picture} crop={crop} icon={icon} className="w-4 h-4 rounded-[3px]" />
            <span className="text-[11px] text-zinc-200 truncate">{name}</span>
            <X className="w-3 h-3 text-zinc-500 ml-auto shrink-0" aria-hidden="true" />
          </div>
        </div>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Phone</p>
        {/* A wallpaper, so it stays dark in the light theme too (900 and 950
            accents never flip; zinc does). */}
        <div className="h-[72px] rounded-xl bg-gradient-to-br from-sky-900 via-indigo-900 to-indigo-950 flex flex-col items-center justify-center gap-1 px-2">
          <IconView picture={picture} crop={crop} icon={icon} pad="p-1.5 bg-zinc-800" className="w-10 h-10 rounded-[11px] shadow-lg" />
          <span className="text-[10px] text-snow truncate max-w-full">{name}</span>
        </div>
      </div>
    </div>
  );
}

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.websiteId
 * @param {string} [props.icon]          the icon as it is now (SVG)
 * @param {string|null} [props.originalIcon]  the icon from before the owner's picture
 * @param {string} [props.siteTitle]
 * @param {(website: {icon_svg: string, drawn_icon_svg: string|null}) => void} props.onSaved
 */
export default function SiteIconEditor({ open, onClose, websiteId, icon = '', originalIcon = null, siteTitle = '', onSaved }) {
  const [picture, setPicture] = useState(null);   // { url, image, w, h }
  const [crop, setCrop] = useState(null);         // { scale, x, y }
  const [busy, setBusy] = useState(null);         // 'open' | 'save' | 'restore'
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [handled, setHandled] = useState(false);  // the picture has been moved or zoomed
  const inputRef = useRef(null);
  const frameRef = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  // Each opening starts from the icon as it is.
  useEffect(() => {
    if (open) return;
    setPicture(null); setCrop(null); setBusy(null); setError(''); setDragOver(false); setHandled(false);
  }, [open]);
  // A picture's object URL lives exactly as long as the picture is in use.
  useEffect(() => () => { if (picture) URL.revokeObjectURL?.(picture.url); }, [picture]);

  // Scroll to zoom. Attached by hand because React's wheel listener is
  // passive: it cannot stop the dialog scrolling underneath.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !picture) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setHandled(true);
      setCrop(c => (c ? zoomCrop(picture, c, c.scale * Math.exp(-e.deltaY * 0.0015)) : c));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [picture]);

  const close = () => { if (!busy || busy === 'open') onClose(); };

  const choose = async (file) => {
    if (!file) return;
    setError('');
    setBusy('open');
    try {
      const next = await openPicture(file);
      setPicture(next);
      setCrop(initialCrop(next));
      setHandled(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  // ── moving and zooming ──────────────────────────────────────────────────────
  // One finger (or the mouse) drags; two pinch. A pinch that loses a finger
  // carries on as a drag from wherever it got to.
  const begin = (from) => {
    const pts = [...pointers.current.values()];
    gesture.current = pts.length >= 2
      ? { kind: 'pinch', dist: distance(pts[0], pts[1]) || 1, crop: from }
      : pts.length === 1 ? { kind: 'drag', x: pts[0].x, y: pts[0].y, crop: from } : null;
  };
  const onPointerDown = (e) => {
    if (!crop) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setHandled(true);
    begin(crop);
  };
  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (g.kind === 'drag' && pts.length === 1) {
      const size = frameRef.current?.clientWidth || 1;
      setCrop(moveCrop(picture, g.crop, (pts[0].x - g.x) / size, (pts[0].y - g.y) / size));
    } else if (g.kind === 'pinch' && pts.length >= 2) {
      setCrop(zoomCrop(picture, g.crop, g.crop.scale * (distance(pts[0], pts[1]) / g.dist)));
    }
  };
  const onPointerEnd = (e) => {
    if (!pointers.current.delete(e.pointerId)) return;
    begin(crop);
  };
  const onKeyDown = (e) => {
    if (!crop) return;
    const step = e.shiftKey ? 0.1 : 0.02;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    let next = null;
    if (moves[e.key]) next = moveCrop(picture, crop, ...moves[e.key]);
    else if (e.key === '+' || e.key === '=') next = zoomCrop(picture, crop, crop.scale * 1.1);
    else if (e.key === '-' || e.key === '_') next = zoomCrop(picture, crop, crop.scale / 1.1);
    if (!next) return;
    e.preventDefault();
    setHandled(true);
    setCrop(next);
  };

  // The slider is logarithmic: each step zooms by the same proportion, so the
  // range from "whole picture" to "close up" feels even all the way along.
  const range = picture ? zoomRange(picture) : null;
  const toSlider = (s) => Math.round((1000 * Math.log(s / range.min)) / Math.log(range.max / range.min));
  const fromSlider = (t) => range.min * Math.pow(range.max / range.min, t / 1000);
  const zoomTo = (scale) => { setHandled(true); setCrop(zoomCrop(picture, crop, scale)); };
  const near = (a, b) => Math.abs(a - b) <= b * 0.01;
  const square = range && near(range.min, range.cover);

  // ── saving ──────────────────────────────────────────────────────────────────
  const save = async () => {
    setError('');
    setBusy('save');
    try {
      const canvas = drawCrop(picture.image, picture, crop);
      const image = canvas ? encodeIcon(canvas) : '';
      if (!image) throw new Error('That picture could not be made into an icon. Try another one.');
      const { website } = await saveSiteIcon(websiteId, image);
      onSaved(website);
      toast.success('Icon updated');
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save the icon.');
    } finally {
      setBusy(null);
    }
  };
  const restore = async () => {
    setError('');
    setBusy('restore');
    try {
      const { website } = await restoreSiteIcon(websiteId);
      onSaved(website);
      toast.success('Back to the original icon');
      onClose();
    } catch (err) {
      setError(err.message || 'Could not change the icon.');
    } finally {
      setBusy(null);
    }
  };

  const own = isOwnIcon(icon);
  const fileInput = (
    <input ref={inputRef} type="file" accept="image/*" className="sr-only"
      aria-label="Upload a photo for the icon"
      onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ''; }} />
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white w-[calc(100vw-1.5rem)] max-w-xl rounded-2xl p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-zinc-800 pr-12">
          <DialogTitle className="text-white text-lg font-bold">Site icon</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm mt-0.5">
            Shown in the browser tab, on your site&rsquo;s card and in every download.
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {!picture ? (
            <>
              <div className="flex items-center gap-4">
                <IconView icon={icon} pad="p-3 bg-zinc-800" className="w-20 h-20 rounded-[22px] ring-1 ring-zinc-700" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{own ? 'Your picture' : 'The icon drawn for your site'}</p>
                  <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                    Use a photo or your logo instead. A face, a logo or a single object reads best at tab size.
                  </p>
                </div>
              </div>

              {/* The input sits inside the label: a click anywhere opens it,
                  and keyboard focus rings the whole drop zone. Only keyboard
                  focus: the dialog focuses the input as it opens, and a ring
                  on every opening read as something already chosen. */}
              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); choose(e.dataTransfer.files?.[0]); }}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-orange-500 ${
                  dragOver ? 'border-orange-500 bg-orange-500/[0.06]' : 'border-zinc-700 hover:border-orange-500/60 bg-zinc-950/40'
                }`}
              >
                {fileInput}
                <span className="w-11 h-11 rounded-2xl bg-orange-500/15 grid place-items-center mb-1">
                  {busy === 'open' ? <Loader2 className="w-5 h-5 text-orange-400 animate-spin" /> : <ImagePlus className="w-5 h-5 text-orange-400" />}
                </span>
                <span className="text-sm font-semibold text-white">{busy === 'open' ? 'Opening your picture…' : 'Upload a photo'}</span>
                <span className="text-xs text-zinc-500">or drop one here · PNG, JPG, WebP or GIF</span>
              </label>

              {own && originalIcon && (
                <button type="button" onClick={restore} disabled={!!busy}
                  className="w-full flex items-center gap-3 rounded-xl border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/40 px-3 py-2.5 text-left transition-colors disabled:opacity-60">
                  <IconView icon={originalIcon} pad="p-1.5 bg-zinc-800" className="w-9 h-9 rounded-lg" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-white">Use the original icon</span>
                    <span className="block text-xs text-zinc-500">The one your site had before your picture</span>
                  </span>
                  {busy === 'restore' ? <Loader2 className="w-4 h-4 text-zinc-400 animate-spin shrink-0" /> : <Undo2 className="w-4 h-4 text-zinc-400 shrink-0" />}
                </button>
              )}

              <Previews icon={icon} title={siteTitle} />
            </>
          ) : (
            <div className="grid sm:grid-cols-[240px_1fr] gap-5 items-start">
              <div>
                <div
                  ref={frameRef}
                  tabIndex={0}
                  role="group"
                  aria-roledescription="crop area"
                  aria-label="The picture in your icon. Drag it to move it, or use the arrow keys. Plus and minus zoom."
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerEnd}
                  onPointerCancel={onPointerEnd}
                  onKeyDown={onKeyDown}
                  style={CHECKER}
                  className="relative w-full max-w-[280px] sm:max-w-none mx-auto aspect-square rounded-[28px] overflow-hidden touch-none select-none cursor-grab active:cursor-grabbing ring-1 ring-zinc-700 outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                >
                  <img src={picture.url} alt="" draggable={false} style={cropStyle(picture, crop)} className="pointer-events-none select-none" />
                  <span aria-hidden="true"
                    className={`pointer-events-none absolute bottom-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium text-snow whitespace-nowrap transition-opacity duration-300 ${handled ? 'opacity-0' : 'opacity-100'}`}>
                    <Move className="w-3 h-3" /> Drag to move · pinch to zoom
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-1.5 max-w-[280px] sm:max-w-none mx-auto">
                  <button type="button" aria-label="Zoom out" onClick={() => zoomTo(crop.scale / 1.25)}
                    className="w-9 h-9 grid place-items-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors">
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <input type="range" min={0} max={1000} step={1} aria-label="Zoom"
                    value={toSlider(crop.scale)} onChange={(e) => zoomTo(fromSlider(Number(e.target.value)))}
                    className="flex-1 min-w-0 accent-orange-500 cursor-pointer" />
                  <button type="button" aria-label="Zoom in" onClick={() => zoomTo(crop.scale * 1.25)}
                    className="w-9 h-9 grid place-items-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors">
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>

                {!square && (
                  <div className="mt-2 grid grid-cols-2 gap-1 rounded-xl bg-zinc-800/60 p-1 max-w-[280px] sm:max-w-none mx-auto" role="group" aria-label="How the picture fits">
                    {[['Fill the square', range.cover], ['Whole picture', range.min]].map(([label, scale]) => (
                      <button key={label} type="button" aria-pressed={near(crop.scale, scale)} onClick={() => zoomTo(scale)}
                        className={`h-8 rounded-lg text-xs font-semibold transition-colors ${near(crop.scale, scale) ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="min-w-0 space-y-4">
                <Previews picture={picture} crop={crop} title={siteTitle} />
                <p className="text-xs text-zinc-500 leading-relaxed">
                  At tab size only the middle of a busy photo is clear. Zoom in on what matters.
                </p>
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400 hover:text-orange-300 cursor-pointer has-[:focus-visible]:underline">
                  {fileInput}
                  <ImagePlus className="w-3.5 h-3.5" /> Choose a different picture
                </label>
              </div>
            </div>
          )}

          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-zinc-800 flex gap-2">
          <button type="button" onClick={close} disabled={busy === 'save' || busy === 'restore'}
            className="flex-1 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold text-zinc-200 transition-colors disabled:opacity-50">
            {picture ? 'Cancel' : 'Close'}
          </button>
          {picture && (
            <button type="button" onClick={save} disabled={!!busy}
              className="flex-1 h-11 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-sm font-semibold text-ink inline-flex items-center justify-center gap-2 transition-colors">
              {busy === 'save' && <Loader2 className="w-4 h-4 animate-spin" />} Save icon
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
